import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Flower2, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';

function RecipesPage() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecipes();
  }, []);

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data.recipes);
    } catch (error) {
      console.error('Failed to load recipes');
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF0F5' }}>
        <div className="animate-pulse" style={{ color: '#1e3a5f' }}>Loading recipes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFF0F5' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-colors"
          >
            <Flower2 className="w-8 h-8 text-primary" />
            <span className="text-xl font-serif font-bold" style={{ color: '#1e3a5f' }}>Radiant 40</span>
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/articles')}
              style={{ color: '#1e3a5f' }}
              className="hover:text-primary transition-colors"
            >
              Articles
            </button>
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-full"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-center" style={{ color: '#1e3a5f' }}>
            Healthy Recipes
          </h1>
          <p className="text-lg text-center mb-12" style={{ color: '#1e3a5f', opacity: 0.7 }}>
            Delicious and nutritious recipes for your wellness journey
          </p>

          {recipes.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
              <p style={{ color: '#1e3a5f', opacity: 0.6 }}>No recipes yet. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-8">
              {recipes.map((recipe) => (
                <article
                  key={recipe.id}
                  className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/recipes/${recipe.slug}`)}
                  data-testid={`recipe-card-${recipe.slug}`}
                >
                  <div className="md:flex">
                    {recipe.images && recipe.images.length > 0 && (
                      <div className="md:w-1/3">
                        <img
                          src={recipe.images[0]}
                          alt={recipe.title}
                          className="w-full h-48 md:h-full object-cover"
                        />
                      </div>
                    )}
                    <div className={`p-6 ${recipe.images && recipe.images.length > 0 ? 'md:w-2/3' : 'w-full'}`}>
                      <div className="flex items-center gap-2 text-sm mb-3" style={{ color: '#1e3a5f', opacity: 0.6 }}>
                        <Calendar className="w-4 h-4" />
                        {formatDate(recipe.created_at)}
                      </div>
                      <h2 className="text-2xl font-serif font-bold mb-3 hover:text-primary transition-colors" style={{ color: '#1e3a5f' }}>
                        {recipe.title}
                      </h2>
                      <p className="line-clamp-3" style={{ color: '#1e3a5f', opacity: 0.7 }}>
                        {recipe.content.replace(/<[^>]*>/g, '').substring(0, 200)}...
                      </p>
                      <div className="mt-4 flex items-center text-primary font-medium">
                        View Recipe <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
        <div className="container mx-auto px-6 text-center" style={{ color: '#1e3a5f', opacity: 0.6 }}>
          <p>© 2026 Sundog Rosie. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default RecipesPage;
